import { ref } from 'vue'

/**
 * Runtime-configurable values injected by the Go server at /config.json.
 * Lets operators point the same pre-built SPA bundle at staging/prod by
 * flipping env vars (e.g. PORTAL_HUB_URL) instead of rebuilding Vue.
 *
 * VITE_HUB_URL is kept as a dev-only fallback so `npm run dev` against a
 * bare portal process still works without a running /config.json endpoint.
 */
export interface RuntimeConfig {
  hub_url: string
  auth_url: string
  payment_url: string
  keycloak_url: string
  admin_url: string
  billing_enabled: boolean
  deployments_enabled: boolean
}

const state = ref<RuntimeConfig | null>(null)
let inflight: Promise<RuntimeConfig> | null = null

const devFallback: RuntimeConfig = {
  hub_url: import.meta.env.VITE_HUB_URL ?? '',
  auth_url: import.meta.env.VITE_AUTH_URL ?? '',
  payment_url: import.meta.env.VITE_PAYMENT_URL ?? '',
  keycloak_url: import.meta.env.VITE_KEYCLOAK_URL ?? '',
  admin_url: import.meta.env.VITE_ADMIN_URL ?? '',
  billing_enabled: false,
  deployments_enabled: false,
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (state.value) return state.value
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const res = await fetch('/config.json', {
        credentials: 'same-origin',
        cache: 'no-store',
      })
      if (!res.ok) throw new Error(`GET /config.json: ${res.status}`)
      const cfg = (await res.json()) as RuntimeConfig
      // Prefer server-provided value; fall back to the Vite env override
      // only when the server returned an empty string (dev without the
      // PORTAL_HUB_URL env var set).
      state.value = {
        hub_url: cfg.hub_url || devFallback.hub_url,
        auth_url: cfg.auth_url || devFallback.auth_url,
        payment_url: cfg.payment_url || devFallback.payment_url,
        keycloak_url: cfg.keycloak_url || devFallback.keycloak_url,
        admin_url: cfg.admin_url || devFallback.admin_url,
        billing_enabled: cfg.billing_enabled ?? false,
        deployments_enabled: cfg.deployments_enabled ?? false,
      }
      return state.value
    } catch {
      state.value = devFallback
      return state.value
    } finally {
      inflight = null
    }
  })()
  return inflight
}

export function useRuntimeConfig() {
  return state
}

export function portalPaymentURL(path: string, portalOrigin: string): string {
	return portalServiceURL(path, 'payment', portalOrigin)
}

function portalServiceURL(path: string, service: string, portalOrigin: string): string {
  let portalURL: URL
  try {
    portalURL = new URL(portalOrigin)
  } catch {
    return path
  }
  if (!portalURL.hostname.startsWith('portal.')) return path
  portalURL.hostname = `${service}.${portalURL.hostname.slice('portal.'.length)}`
  return new URL(path, portalURL).toString()
}

export function authURL(path: string): string {
  const base = state.value?.auth_url || devFallback.auth_url
  if (base) return new URL(path, base).toString()
  if (typeof window === 'undefined') return path
  return portalServiceURL(path, 'auth', window.location.origin)
}

export function paymentURL(path: string): string {
	const base = state.value?.payment_url || devFallback.payment_url
	if (base) return new URL(path, base).toString()
	if (typeof window === 'undefined') return path
	return portalPaymentURL(path, window.location.origin)
}
